'use client';

import { Composite } from '@floating-ui/react';
import { forwardRef, useCallback, useMemo, useState } from 'react';

import { css, cx } from '@/styled-system/css';

import { useChildrenOfType } from '../../hooks/use-children-of-type';
import { useControllableState } from '../../hooks/use-controllable-state';
import { TabsRootContext } from './context/tabs-root.context';
import type { TabsRootPropsInterface } from './types/tabs-root.types';

/**
 * A `Tabs.Root` component acts as the container for `Tabs.List` and `Tabs.Panels`.
 *
 * If you aren't using `Tabs.Panel` components, you can use `Tabs.List` without this `Tabs.Root` parent,
 * if you prefer.
 *
 * @subcomponents ['Tabs.List', 'Tabs.Panel', 'Tabs.Tab', 'Tabs.TabIcon']
 * @since 8.10.0
 */

export const TabsRoot = forwardRef<HTMLDivElement, TabsRootPropsInterface>(
  (props: TabsRootPropsInterface, forwardedRef) => {
    const { children, ...restProps } = props;

    const {
      appearance = 'default',
      className,
      css: cssProp,
      defaultSelected,
      fluid = false,
      disabled = false,
      manualActivation,
      onSelectedChange,
      selected,
      ...rest
    } = restProps;

    const [selectedState, setSelectedState] = useControllableState({
      prop: selected,
      defaultProp: defaultSelected,
      onChange: onSelectedChange,
    });

    const allTabs = useChildrenOfType(children, 'Tabs.Tab');

    const initialTabIndex = useMemo(() => {
      return selectedState ? allTabs.findIndex((tab) => tab.props.value === selectedState) : 0;
    }, [allTabs, selectedState]);

    const [activeIndex, setActiveIndex] = useState(initialTabIndex);

    const onTabsFocusChange = useCallback(
      (index: number) => {
        setActiveIndex(index);

        if (!manualActivation) {
          // If the tab at the new index is disabled, don't change panel content
          const tabAtNewIndex = allTabs?.[index];
          if (tabAtNewIndex?.props.disabled) {
            return;
          }
          setSelectedState(allTabs?.[index]?.props.value);
        }
      },
      [allTabs, manualActivation, setSelectedState],
    );

    return (
      <TabsRootContext.Provider
        value={{
          appearance,
          fluid,
          disabled,
          manualActivation,
          selected: selectedState,
          setSelected: setSelectedState,
        }}
      >
        {/* Intentionally not allowing user overrides for Composite */}
        <Composite
          loop
          activeIndex={activeIndex}
          onNavigate={onTabsFocusChange}
          disabledIndices={[]}
          orientation="horizontal"
        >
          <div
            ref={forwardedRef}
            data-c11n-component="Tabs.Root"
            data-c11n-version={__C11N_VERSION__}
            className={cx('c11n', css(cssProp), className)}
            {...rest}
          >
            {children}
          </div>
        </Composite>
      </TabsRootContext.Provider>
    );
  },
);

TabsRoot.displayName = 'Tabs.Root';
